import clsx from 'clsx';
import { HTMLAttributes, ReactNode } from 'react';

import styles from './EntityDetails.module.scss';

export interface EntityDetailsProps extends HTMLAttributes<HTMLDivElement> {
  /** Entity name (rendered as the primary title). Pass a `ReactNode` for things like a link or styled heading. */
  entityNameComponent: ReactNode;
  /** Formatted ticker / tag rendered under the entity name (e.g. `"AAPL · NASDAQ"`). */
  formattedTicker: string;
  /** Left-side image (typically a logo or country flag). */
  leftImgNode: ReactNode;
  /** Element rendered next to the entity name in the regular layout, or to the right of price in condensed layout. */
  rightNode?: ReactNode;
  /** Optional action button (e.g. add-to-watchlist) rendered between name and price. */
  buttonNode?: ReactNode;
  /** Right-side price-display block (typically a `LivePrice` or similar). */
  priceDisplayNode?: ReactNode;
  /** Use the compact two-line layout. Defaults to `false`. */
  isCondensed?: boolean;
  watchlistPillsNode?: ReactNode;
  /** Reserved for highlighting the action button when it's in an "active" state. */
  isButtonActive?: boolean;
}

/**
 * Header strip used by widget modules to identify the active entity:
 * logo + name + ticker, an optional action button, and a live price
 * block.
 *
 * `isCondensed` swaps the layout to a tighter two-line variant where
 * `rightNode` moves alongside the price. Visual styling is owned by
 * the SCSS module — pass through ready-rendered children.
 */
export const EntityDetails = ({
  entityNameComponent,
  formattedTicker,
  leftImgNode,
  rightNode,
  buttonNode,
  priceDisplayNode,
  isCondensed = false,
  watchlistPillsNode,
  ...rest
}: EntityDetailsProps) => {
  return (
    <div
      className={styles.entityDetailsRoot}
      data-testid="module-entity-details"
      {...rest}
    >
      <div
        className={clsx(styles.contentWrapper, {
          [styles.condensed]: isCondensed,
        })}
        data-content-wrapper
      >
        <div className={styles.tickerWrapper}>
          <span className={styles.ticker}>
            {leftImgNode && (
              <div className={styles.leftImageContainer}>{leftImgNode}</div>
            )}
            <div
              className={clsx(styles.titleContainer, {
                [styles.condensed]: isCondensed,
              })}
              data-title-container
            >
              <div className={styles.titleRow}>
                {entityNameComponent}
                {!!rightNode && !isCondensed && rightNode}
              </div>
              <p className={styles.tagWrapper}>
                {formattedTicker}
                {formattedTicker && watchlistPillsNode && (
                  <span aria-hidden="true">·</span>
                )}
                {watchlistPillsNode}
              </p>
            </div>
          </span>
        </div>
        <div className={styles.watchlistWrapper} data-watchlist-wrapper>
          {buttonNode}
        </div>
        <div className={styles.priceWrapper}>
          {priceDisplayNode}
          {!!rightNode && isCondensed && rightNode}
        </div>
      </div>
    </div>
  );
};
